import { useEffect, useState } from "react";
import api from "@/lib/api";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { ScoreBars } from "@/components/charts/ScoreBars";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FinePrint } from "@/components/FinePrint";
import {
  BarChart3, ScrollText, Loader2, Users, TrendingUp, Scale, Heart, Mail, Phone, CheckCircle2, RotateCcw, Compass, Award, Network, UserCog, UserPlus, ClipboardList, ListChecks, Lightbulb, Lock, HelpCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, HOME_FOR } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from "recharts";

const ADMIN_NAV = [
  { to: "/app/admin", label: "Analytics", icon: BarChart3 },
  { to: "/app/admin/occupations", label: "Career Profiles", icon: Network },
  { to: "/app/admin/assessments", label: "Assessments", icon: Compass },
  { to: "/app/admin/certifications", label: "Certifications", icon: Award },
  { to: "/app/admin/registrations", label: "Registration Requests", icon: UserPlus },
  { to: "/app/admin/tasks", label: "Administrative Tasks", icon: ClipboardList },
  { to: "/app/tasks", label: "My Tasks", icon: ListChecks },
  { to: "/app/admin/suggestions", label: "Suggestions", icon: Lightbulb },
  { to: "/app/admin/users", label: "User Accounts", icon: UserCog },
  { to: "/app/admin/communications", label: "Communications", icon: Mail },
  { to: "/app/admin/supporters", label: "Supporters", icon: Heart },
  { to: "/app/admin/audit", label: "Audit Trail", icon: ScrollText },
  { to: "/app/help", label: "Help & Documentation", icon: HelpCircle },
];
export { ADMIN_NAV };

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <Card className="p-5 card-elevate">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#003057]/6"><Icon className="h-5 w-5 text-[#003057]" /></div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{label}</p>
          <p className="font-mono-num text-2xl font-bold text-[#003057]">{value}</p>
          {sub && <p className="text-xs text-slate-400">{sub}</p>}
        </div>
      </div>
    </Card>
  );
}

export function PreviewLocked() {
  const { user, exitPreview } = useAuth();
  const navigate = useNavigate();
  const back = async () => {
    try {
      if (user?.preview) { const u = await exitPreview(); navigate(HOME_FOR[u.role]); }
      else navigate(HOME_FOR[user?.real_role || user?.role] || "/");
    } catch (_) {}
  };
  return (
    <Card className="mx-auto max-w-lg p-10 text-center card-elevate" data-testid="preview-locked">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100"><Lock className="h-6 w-6 text-amber-700" /></div>
      <h3 className="font-heading text-lg font-bold text-[#003057]">Website Administrator access required</h3>
      <p className="mt-2 text-sm text-slate-500">This section isn't available in preview. You're signed in as {user?.real_name || "your account"} ({user?.real_role || user?.role}).</p>
      <Button onClick={back} data-testid="preview-locked-exit" className="mt-5 rounded-full bg-[#003057] hover:bg-[#00223f]">Back to my dashboard</Button>
    </Card>
  );
}

export default function AdminHome() {
  const [a, setA] = useState(null);
  const [reg, setReg] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics").then(({ data }) => setA(data)).catch(() => {});
    api.get("/admin/registration/summary").then(({ data }) => setReg(data)).catch(() => {});
  }, []);

  return (
    <DashboardShell title="Institutional Analytics" subtitle="Cohort benchmarks, program comparisons & equity" nav={ADMIN_NAV}>
      {!a && <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003057]" /></div>}
      {a && (
        <div className="space-y-6">
          {reg && reg.open_total > 0 && (
            <Link to="/app/admin/registrations" data-testid="pending-registrations-alert"
              className="flex items-center justify-between rounded-xl border border-amber-300 bg-amber-50 px-5 py-4 transition-colors hover:bg-amber-100">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-200/70"><UserPlus className="h-5 w-5 text-amber-800" /></div>
                <div>
                  <p className="font-bold text-amber-900">Pending Registration Requests</p>
                  <p className="text-sm text-amber-700">{reg.pending_approval} awaiting review{reg.more_info ? ` · ${reg.more_info} awaiting applicant` : ""}</p>
                </div>
              </div>
              <span className="rounded-full bg-amber-500 px-3 py-1 text-sm font-bold text-white">{reg.open_total}</span>
            </Link>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Stat icon={Users} label="Students" value={a.total_students} sub="in cohort" />
            <Stat icon={TrendingUp} label="Average TSB" value={a.avg_tsb.toFixed(2)} sub={`${a.avg_tsb_100}/100`} />
            <Stat icon={Scale} label="Levels represented" value={a.level_distribution.filter((l) => l.count > 0).length} sub="of 4" />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card className="p-6 card-elevate">
              <h3 className="mb-4 font-heading text-lg font-bold text-[#003057]">Cohort TSB distribution</h3>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={a.level_distribution} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#334155" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                    <Tooltip cursor={{ fill: "rgba(0,48,87,0.04)" }} />
                    <Bar dataKey="count" radius={[8, 8, 0, 0]} barSize={54}>
                      {a.level_distribution.map((l, i) => <Cell key={i} fill={l.hex} />)}
                      <LabelList dataKey="count" position="top" style={{ fontSize: 12, fontWeight: 700, fill: "#334155" }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-6 card-elevate">
              <h3 className="mb-4 font-heading text-lg font-bold text-[#003057]">Domain averages (cohort)</h3>
              <ScoreBars items={a.domain_averages.map((d) => ({ name: d.name, value: d.avg, color: d.color }))} height={260} />
            </Card>
          </div>

          <Card className="p-6 card-elevate">
            <h3 className="mb-4 font-heading text-lg font-bold text-[#003057]">Program comparison</h3>
            <ScoreBars
              items={a.program_comparison.map((p) => ({ name: p.program, value: p.avg_tsb, color: "#005291" }))}
              height={Math.max(220, a.program_comparison.length * 44)} />
          </Card>

          <Card className="p-6 card-elevate">
            <h3 className="mb-1 font-heading text-lg font-bold text-[#003057]">Equity-gap view</h3>
            <p className="mb-4 text-sm text-slate-500">Average TSB by student group vs. comparison group. A positive gap signals an opportunity to close.</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {a.equity_gaps.map((g) => (
                <div key={g.group} className="rounded-xl border border-slate-200 p-4">
                  <p className="font-semibold text-[#003057]">{g.group}</p>
                  <div className="mt-2 flex items-end gap-4">
                    <div>
                      <p className="font-mono-num text-2xl font-bold text-[#005291]">{g.avg_tsb.toFixed(2)}</p>
                      <p className="text-xs text-slate-400">group avg</p>
                    </div>
                    <div>
                      <p className="font-mono-num text-lg font-semibold text-slate-500">{g.comparison.toFixed(2)}</p>
                      <p className="text-xs text-slate-400">comparison</p>
                    </div>
                    <div className={`ml-auto rounded-full px-3 py-1 text-sm font-bold ${g.gap > 0.05 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                      {g.gap > 0 ? "-" : "+"}{Math.abs(g.gap).toFixed(2)} gap
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
          <FinePrint>
            Cohort distributions, program comparisons and equity gaps are computed live over mock student data for
            illustration only — not real institutional results. Domain roll-up weights are a neutral prototype default
            pending validation. See Research & Methodology for sources and limitations.
          </FinePrint>
        </div>
      )}
    </DashboardShell>
  );
}

export function AdminSupporters() {
  const [rows, setRows] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const load = () => api.get("/admin/sponsors").then(({ data }) => setRows(data))
    .catch((e) => { if (e.response?.status === 403) setForbidden(true); else setRows([]); });
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/sponsors/${id}`, { status });
      setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(status === "contacted" ? "Marked as contacted" : "Reopened");
    } catch {
      toast.error("Could not update status");
    }
  };

  const newCount = rows?.filter((r) => r.status === "new").length ?? 0;

  return (
    <DashboardShell title="Supporter Inquiries" subtitle="Organizations interested in supporting GPA+" nav={ADMIN_NAV}>
      {forbidden && <PreviewLocked />}
      {!forbidden && !rows && <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003057]" /></div>}
      {rows && rows.length === 0 && (
        <Card className="p-8 text-center text-slate-500 card-elevate" data-testid="supporters-empty">
          No supporter inquiries yet. Submissions from the Supporters &amp; Partners page will appear here.
        </Card>
      )}
      {rows && rows.length > 0 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Stat icon={Heart} label="Total inquiries" value={rows.length} />
            <Stat icon={Mail} label="Awaiting follow-up" value={newCount} sub="marked new" />
            <Stat icon={CheckCircle2} label="Contacted" value={rows.length - newCount} />
          </div>
          <Card className="p-2 card-elevate sm:p-4">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Received</TableHead><TableHead>Organization</TableHead><TableHead>Contact</TableHead>
                <TableHead>Level</TableHead><TableHead>Referred by</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`supporter-row-${r.id}`}>
                    <TableCell className="font-mono-num text-xs text-slate-500">{r.created_at?.slice(0, 10)}</TableCell>
                    <TableCell>
                      <p className="font-semibold text-[#003057]">{r.company}</p>
                      {r.message && <p className="mt-0.5 max-w-xs truncate text-xs text-slate-400">{r.message}</p>}
                    </TableCell>
                    <TableCell className="text-sm">
                      <p className="font-medium text-[#003057]">{r.contact_name}</p>
                      <a href={`mailto:${r.email}`} className="flex items-center gap-1 text-xs text-[#005291] hover:underline"><Mail className="h-3 w-3" />{r.email}</a>
                      {r.phone && <span className="flex items-center gap-1 text-xs text-slate-400"><Phone className="h-3 w-3" />{r.phone}</span>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{r.tier}</TableCell>
                    <TableCell className="text-sm">
                      {r.referred_by_name
                        ? <span className="inline-flex items-center gap-1 rounded-full bg-[#78be21]/15 px-2 py-0.5 text-xs font-semibold text-[#3d6b0e]">{r.referred_by_name}</span>
                        : <span className="text-xs text-slate-300">—</span>}
                    </TableCell>
                    <TableCell>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${r.status === "contacted" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                        {r.status === "contacted" ? "Contacted" : "New"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {r.status === "new" ? (
                        <Button size="sm" data-testid={`supporter-contacted-${r.id}`} onClick={() => setStatus(r.id, "contacted")}
                          className="rounded-full bg-[#78be21] text-xs font-bold text-[#00243f] hover:bg-[#6aac1c]">
                          <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Mark contacted
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" data-testid={`supporter-reopen-${r.id}`} onClick={() => setStatus(r.id, "new")}
                          className="rounded-full text-xs font-semibold">
                          <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reopen
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}
    </DashboardShell>
  );
}

export function AdminAudit() {
  const [logs, setLogs] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  useEffect(() => {
    api.get("/admin/audit").then(({ data }) => setLogs(data))
      .catch((e) => { if (e.response?.status === 403) setForbidden(true); else setLogs([]); });
  }, []);
  return (
    <DashboardShell title="Audit Trail" subtitle="Every score-affecting change is logged (esp. employer 360)" nav={ADMIN_NAV}>
      {forbidden && <PreviewLocked />}
      {!forbidden && !logs && <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003057]" /></div>}
      {logs && logs.length === 0 && (
        <Card className="p-8 text-center text-slate-500 card-elevate" data-testid="audit-empty">
          No changes recorded yet. Submit an Employer 360 review to see it appear here.
        </Card>
      )}
      {logs && logs.length > 0 && (
        <Card className="p-2 card-elevate sm:p-4">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Time (UTC)</TableHead><TableHead>Actor</TableHead><TableHead>Action</TableHead><TableHead>Detail</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {logs.map((l, i) => (
                <TableRow key={i} data-testid={`audit-row-${i}`}>
                  <TableCell className="font-mono-num text-xs text-slate-500">{l.timestamp?.slice(0, 19).replace("T", " ")}</TableCell>
                  <TableCell className="text-sm"><span className="font-semibold text-[#003057]">{l.actor_email}</span><span className="ml-1 text-xs text-slate-400">({l.actor_role})</span></TableCell>
                  <TableCell><span className="rounded-full bg-[#003057]/8 px-2.5 py-0.5 text-xs font-semibold text-[#003057]">{l.action}</span></TableCell>
                  <TableCell className="text-xs text-slate-500">{JSON.stringify(l.detail)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </DashboardShell>
  );
}
